import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";

import { genId } from "../core/services/genId";

import { AddressDomain } from "../builder/models/domain.model";
import { deepClone } from "../builder/models/editor.model";
import { AssociateDocumentKind, Letter, LetterAddress, LetterAttachment, LetterCreationRequest, LetterSearchCriteria, LetterStatus, LetterStatusUpdateRequest, SaveLetterWithAttachmentsRequest } from "./letters.model";
import { TextTemplateRuleWithOnBaseKeywordName } from "../common/models/flow.dto";
import { IDocument, UploadedFile } from "../common/services/document-management.service";
import { IReportContextValue } from "../models/comodel/mergefieldsmodel.model";

@Injectable()
export class LettersService {

    constructor(private readonly http: HttpClient) {}

    public createLetter(mail: Letter, document: IDocument, letterContent: string, reportContextValues: IReportContextValue[], letterAttachments: LetterAttachment[]): Observable<Letter> {
        let letterCreationRequest = {
            letterAttachments: letterAttachments,
            letterContent: letterContent,
            mail: mail,
            reportContextValues: reportContextValues,
            temporaryDocument: document
        } as LetterCreationRequest;

        return this.http.post<Letter>(`/api/letters/createLetter`, letterCreationRequest);
    }

    public updateLetterStatus(changes: LetterStatusUpdateRequest[]): Observable<boolean> {
        return this.http.post<boolean>(`/api/updateLetterStatus/`, changes);
    }

    public save(letter: Letter): Observable<Letter> {
        return this.http.post<Letter>(`/api/letters/${letter.id}`, letter);
    }

    public saveWithAttachments(letter: Letter, letterContent: string, letterAttachments: LetterAttachment[]): Observable<any> {
        let letterSaveWithAttachmentsRequest = {
            mail: letter,
            updatedLetterContent: letterContent,
            letterAttachments: letterAttachments
        } as SaveLetterWithAttachmentsRequest;
        return this.http.post(`/api/letters/saveLetterWithAttachments`, letterSaveWithAttachmentsRequest);
    }

    public saveAndGenerateLabel(letter: Letter): Observable<Letter> {
        return this.http.post<Letter>(`/api/letters/generateLabel/${letter.id}`, letter);
    }

    public get(id: string): Observable<Letter> {
        return this.http.get<Letter>(`/api/letters/${id}`);
    }

    public getMailAttachments(id: string): Observable<LetterAttachment[]> {
        return this.http.get<LetterAttachment[]>(`/api/letters/getAttachments/${id}`);
    }

    public getHtmlContent(id: string, includeTrackingFields: boolean): Observable<any> {
        return this.http.get<string>(`/api/letters/${id}/previewHtml?includeTrackingFields=${includeTrackingFields ? 'true' : 'false'}`);
    }

    public previewUrl(id:string, includeTrackingFields:boolean): string {
        return `/api/letters/${id}/preview?includeTrackingFields=${includeTrackingFields? 'true' : 'false'}`;
    }

    public find(criteria: LetterSearchCriteria): Observable<Letter[]> {
        const query: string = this._buildQueryString(criteria);
        return this.http.get<Letter[]>(`/api/letters/search?${query}`);
    }

    public checkForUpdatedLetterStatus(id: string): Observable<Letter> {
        return this.http.get<Letter>(`/api/letters/update-certified-mail-item/${id}`);
    }

    public testCheckStatusScheduledTask()  {
        return this.http.get(`/api/schedule/checkAndUpdateCertifiedMailStatus`);
    }

    //This method partially clones a letter, and creates it.
    public clone(letter:Letter): Observable<Letter> {
        return this.http.post<Letter>(`/api/letters/clone`, letter);
    }

    private _buildQueryString(criteria: LetterSearchCriteria): string {
        let params = new URLSearchParams();
            for (let key in criteria) {
                if (criteria.hasOwnProperty(key)) {
                    let value = criteria[key]
                    if (value instanceof Array) {
                        for (let index in value) {
                            params.append(key + '[' + index + ']', value[index])
                        }
                    }
                    else if (value instanceof Date) {
                        params.append(key, value.toISOString());
                    }
                    else {
                        params.append(key, value.toString());
                    }
                }
            }
        return params.toString();
    }

    public associateDocumentUrl(id: string, docKind: AssociateDocumentKind) {
        return `/api/letters/${id}/${docKind}`;
    }

    public mapDomainToSnailMailAddress(domain: AddressDomain): LetterAddress {
        return {
            title: domain.Definition,
            name1: domain.Name,
            name2: domain.NameLine2,
            name3: domain.NameLine3,
            address1: domain.Address1,
            address2: domain.Address2 || null,
            address3: domain.Address3 || null,
            city: domain.City,
            state: domain.State,
            zipCode: domain.ZipCode,
            zipPlus4: domain.ZipPlus4
        } as LetterAddress;
    }

    public extractRules(content: string, beginTag: string, endTag: string): TextTemplateRuleWithOnBaseKeywordName[] {
        function escapeToken(text) { return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&'); }
        let re = new RegExp(escapeToken(beginTag) + ".*?" + escapeToken(endTag), 'g');
        let matches = content.match(re);
        let distinctMatches = [];

        if (matches) {
            for (let x = 0; x < matches.length; x++) {
                if (distinctMatches.filter(a => a == matches[x]).length == 0)
                    distinctMatches.push(matches[x]);
            }
        }

        return (distinctMatches || [])
            .map(p => {
                return {
                    ruleId: "",
                    name: p.trim(),
                    expression: p.substring(beginTag.length, p.length - beginTag.length),
                    parseTree: null,
                    onBaseKeywordName: "", 
                    compiledValue: ""
                };
            });
    }

}
